import { ChangeEvent, MouseEvent, ReactNode } from "react";
import { ViewItem } from "../../../../Components/Views/ViewItem";
import { controllers } from "../../../../Controllers/Controllers";
import { STATUS_LABELS } from "../../../../DataTypes/leads";
import { StatusLogType, StatusLogsPagination, TelegramReportDetailType, TelegramReportType } from "../../../../DataTypes/report";
import { getRoundedLocalDateTimeString } from "../../../../Helpers/Helpers";
import '../outreach-styles.css'
import '../Categories/categories-styles.css'
import './report-styles.css'

// Mirrors LeadStatusChangeLog.SOURCE_CHOICES server-side.
const SOURCE_LABELS: Record<string, string> = {
    manual: 'Manual',
    email_sent: 'Email Sent',
    email_reply: 'Reply Detected',
    unsubscribe: 'Unsubscribe Link',
    other: 'Other',
}

type ResearchCategoryGroup = {
    category: string;
    logs: StatusLogType[];
}

const isResearchContacted = (log: StatusLogType): boolean =>
    log.new_status === 'contacted' && !!log.lead_is_from_research

const extractLogs = (data: any): StatusLogType[] => data?.logs || []

type ReportPageState = {
    logs: StatusLogType[];
    loading: boolean;
    error: string | null;
    needsAuth: boolean;
    checkedIds: number[];
    additionalMessage: string;
    markingAllRead: boolean;
    deletingLogId: number | null;
    sending: boolean;
    sendError: string | null;
    sendMessage: string | null;

    sentReports: TelegramReportType[];
    sentReportsPagination: StatusLogsPagination | null;
    sentReportsPage: number;
    sentReportsLoading: boolean;
    sentReportsError: string | null;

    viewingReport: TelegramReportDetailType | null;
    viewingReportLoading: boolean;
    viewingReportError: string | null;
    showRawMessage: boolean;
}

class ReportPage extends ViewItem {
    state: ReportPageState;

    constructor(props: any) {
        super(props)
        this.state = {
            logs: [],
            loading: true,
            error: null,
            needsAuth: false,
            checkedIds: [],
            additionalMessage: '',
            markingAllRead: false,
            deletingLogId: null,
            sending: false,
            sendError: null,
            sendMessage: null,

            sentReports: [],
            sentReportsPagination: null,
            sentReportsPage: 1,
            sentReportsLoading: true,
            sentReportsError: null,

            viewingReport: null,
            viewingReportLoading: false,
            viewingReportError: null,
            showRawMessage: false,
        }
    }

    componentDidMount(): void {
        this.loadLogs()
        this.loadSentReports()
    }

    // ── Derived groupings ──────────────────────────────────────
    // Contacted-via-research entries are pulled out of the flat table and
    // shown as their own category-grouped block; everything else (including
    // regular outreach-contacted entries) renders in the table as before.

    get researchContactedLogs(): StatusLogType[] {
        return this.state.logs.filter(isResearchContacted)
    }

    get tableLogs(): StatusLogType[] {
        return this.state.logs.filter(l => !isResearchContacted(l))
    }

    get researchContactedGroups(): ResearchCategoryGroup[] {
        const groups: Record<string, StatusLogType[]> = {}
        const order: string[] = []
        for (const log of this.researchContactedLogs) {
            const category = log.lead_category || 'Uncategorized'
            if (!groups[category]) {
                groups[category] = []
                order.push(category)
            }
            groups[category].push(log)
        }
        return order.map(category => ({ category, logs: groups[category] }))
    }

    loadLogs = async () => {
        this.setState({ loading: true, error: null, needsAuth: false })
        if (!controllers().auth.userToken) {
            this.setState({ loading: false, needsAuth: true })
            return
        }
        const response = await controllers().report.getUnreportedLogs()
        if (!response || response.statusCode !== 200) {
            this.setState({ loading: false, error: response?.error || 'Failed to load status updates.' })
            return
        }
        const logs = extractLogs(response.data)
        // Pre-checked by default — the workflow is "uncheck what you don't
        // want sent," not opt-in per entry.
        this.setState({ loading: false, logs, checkedIds: logs.map(l => l.id) })
    }

    toggleChecked = (id: number) => {
        this.setState((prev: ReportPageState) => ({
            checkedIds: prev.checkedIds.includes(id)
                ? prev.checkedIds.filter(i => i !== id)
                : [...prev.checkedIds, id],
        }))
    }

    // Generalized select-all: toggles only the ids within the given subset
    // (a table page, or a single research category group) rather than every
    // log on the page, so the table and the research groups can each have
    // their own independent "select all" without stomping on each other.
    toggleSelectAllFor = (logs: StatusLogType[]) => {
        const ids = logs.map(l => l.id)
        if (!ids.length) return
        this.setState((prev: ReportPageState) => {
            const allSelected = ids.every(id => prev.checkedIds.includes(id))
            return {
                checkedIds: allSelected
                    ? prev.checkedIds.filter(id => !ids.includes(id))
                    : Array.from(new Set([...prev.checkedIds, ...ids])),
            }
        })
    }

    markRowRead = async (log: StatusLogType) => {
        if (log.is_read) return
        const response = await controllers().report.markLogRead(log.id)
        if (response && response.statusCode === 200) {
            this.setState((prev: ReportPageState) => ({
                logs: prev.logs.map(l => l.id === log.id ? { ...l, is_read: true } : l),
            }))
        }
    }

    // Same idea as markRowRead, but for a whole research category group at
    // once — clicking a category row marks every unread entry in it read.
    markGroupRead = async (logs: StatusLogType[]) => {
        const unread = logs.filter(l => !l.is_read)
        if (!unread.length) return
        await Promise.all(unread.map(l => controllers().report.markLogRead(l.id)))
        const readIds = new Set(unread.map(l => l.id))
        this.setState((prev: ReportPageState) => ({
            logs: prev.logs.map(l => readIds.has(l.id) ? { ...l, is_read: true } : l),
        }))
    }

    handleMarkAllRead = async () => {
        this.setState({ markingAllRead: true })
        const response = await controllers().report.markAllLogsRead()
        if (response && response.statusCode === 200) {
            this.setState((prev: ReportPageState) => ({
                logs: prev.logs.map(l => ({ ...l, is_read: true })),
            }))
        }
        this.setState({ markingAllRead: false })
    }

    handleAdditionalMessageChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
        this.setState({ additionalMessage: e.target.value })
    }

    // Soft-delete — stays in the database, just excluded from normal
    // listings and no longer reportable. Doesn't touch checkedIds since the
    // row is gone from `logs` entirely, and Send filters against that.
    handleTrashLog = async (log: StatusLogType, e: MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation()
        const label = `${log.lead_company_name || 'This lead'}: ${STATUS_LABELS[log.old_status] || log.old_status || 'New'} → ${STATUS_LABELS[log.new_status] || log.new_status}`
        const confirmed = window.confirm(`Remove this update from the report list?\n\n${label}`)
        if (!confirmed) return
        this.setState({ deletingLogId: log.id })
        const response = await controllers().report.trashLog(log.id)
        if (response && response.statusCode === 200) {
            this.setState((prev: ReportPageState) => ({
                logs: prev.logs.filter(l => l.id !== log.id),
                checkedIds: prev.checkedIds.filter(id => id !== log.id),
            }))
        }
        this.setState({ deletingLogId: null })
    }

    loadSentReports = async () => {
        const { sentReportsPage } = this.state
        this.setState({ sentReportsLoading: true, sentReportsError: null })
        const response = await controllers().report.listSentReports({ page: sentReportsPage, per_page: 10 })
        if (!response || response.statusCode !== 200) {
            this.setState({ sentReportsLoading: false, sentReportsError: response?.error || 'Failed to load sent reports.' })
            return
        }
        this.setState({
            sentReportsLoading: false,
            sentReports: response.data?.reports || [],
            sentReportsPagination: response.data?.pagination || null,
        })
    }

    goToSentReportsPage = (page: number) => {
        const { sentReportsPagination } = this.state
        if (sentReportsPagination && (page < 1 || page > sentReportsPagination.total_pages)) return
        this.setState({ sentReportsPage: page }, this.loadSentReports)
    }

    openReportDetail = async (report: TelegramReportType) => {
        this.setState({ viewingReport: null, viewingReportLoading: true, viewingReportError: null, showRawMessage: false })
        const response = await controllers().report.getSentReportDetail(report.id)
        if (!response || response.statusCode !== 200) {
            this.setState({ viewingReportLoading: false, viewingReportError: response?.error || 'Failed to load report.' })
            return
        }
        this.setState({ viewingReportLoading: false, viewingReport: response.data })
    }

    closeReportDetail = () => {
        this.setState({ viewingReport: null, viewingReportError: null, viewingReportLoading: false })
    }

    toggleRawMessage = () => {
        this.setState((prev: ReportPageState) => ({ showRawMessage: !prev.showRawMessage }))
    }

    handleSend = async () => {
        const { checkedIds, additionalMessage } = this.state
        if (!checkedIds.length) return
        this.setState({ sending: true, sendError: null, sendMessage: null })
        const response = await controllers().report.sendReport(checkedIds, additionalMessage.trim())
        if (!response || response.statusCode !== 200) {
            this.setState({ sending: false, sendError: response?.error || 'Failed to send report.' })
            return
        }
        const sentCount = checkedIds.length
        this.setState((prev: ReportPageState) => ({
            sending: false,
            sendMessage: `Sent ${sentCount} update${sentCount === 1 ? '' : 's'} to Telegram.`,
            // Only the sent entries are gone from "unreported" — anything left
            // unchecked stays for next time.
            logs: prev.logs.filter(l => !checkedIds.includes(l.id)),
            checkedIds: [],
            additionalMessage: '',
        }))
    }

    renderTable = (logs: StatusLogType[]) => {
        const { checkedIds, deletingLogId } = this.state
        const allChecked = logs.length > 0 && logs.every(l => checkedIds.includes(l.id))
        return (
            <div className="outreach-table-wrap">
                <table className="outreach-table">
                    <thead>
                        <tr>
                            <th className="report-checkbox-col">
                                <input
                                    type="checkbox"
                                    checked={allChecked}
                                    onChange={() => this.toggleSelectAllFor(logs)}
                                    aria-label="Select all"
                                />
                            </th>
                            <th>Lead</th>
                            <th>Status Change</th>
                            <th>Source</th>
                            <th>Note</th>
                            <th>Changed By</th>
                            <th>Date</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {logs.map(log => (
                            <tr
                                key={log.id}
                                className={!log.is_read ? 'report-row-unread' : ''}
                                onClick={() => this.markRowRead(log)}
                            >
                                <td className="report-checkbox-col">
                                    <input
                                        type="checkbox"
                                        checked={checkedIds.includes(log.id)}
                                        onClick={(e) => e.stopPropagation()}
                                        onChange={() => this.toggleChecked(log.id)}
                                        aria-label={`Include ${log.lead_company_name}`}
                                    />
                                </td>
                                <td>
                                    <div className="outreach-company-cell">
                                        <span className="outreach-company-name">
                                            {!log.is_read && <span className="report-unread-dot" />}
                                            {log.lead_company_name}
                                        </span>
                                    </div>
                                </td>
                                <td>
                                    <div className="report-status-change">
                                        <span className={`report-status-chip lead-status-${log.old_status}`}>
                                            {STATUS_LABELS[log.old_status] || log.old_status || '—'}
                                        </span>
                                        <span className="report-status-arrow">→</span>
                                        <span className={`report-status-chip lead-status-${log.new_status}`}>
                                            {STATUS_LABELS[log.new_status] || log.new_status}
                                        </span>
                                    </div>
                                </td>
                                <td className="outreach-muted">{SOURCE_LABELS[log.source] || log.source}</td>
                                <td>
                                    {log.note ? (
                                        <div className="outreach-notes-cell">
                                            <span className="outreach-notes-truncated">{log.note}</span>
                                            <div className="outreach-notes-tooltip">{log.note}</div>
                                        </div>
                                    ) : (
                                        <span className="outreach-muted">—</span>
                                    )}
                                </td>
                                <td className="outreach-muted">{log.changed_by?.email || 'System'}</td>
                                <td className="outreach-muted">{getRoundedLocalDateTimeString(log.created_at)}</td>
                                <td>
                                    <button
                                        className="outreach-edit-btn category-delete-btn"
                                        onClick={(e) => this.handleTrashLog(log, e)}
                                        disabled={deletingLogId === log.id}
                                    >
                                        Delete
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        )
    }

    // Contacted-via-research entries, grouped by lead category, each with a
    // count and its own select-all checkbox — mirrors the "Contacted with
    // research" block in the Telegram report rather than listing every lead
    // individually.
    renderResearchContactedSummary = () => {
        const groups = this.researchContactedGroups
        if (!groups.length) return null

        const { checkedIds } = this.state
        const allResearchLogs = this.researchContactedLogs
        const allChecked = allResearchLogs.length > 0 && allResearchLogs.every(l => checkedIds.includes(l.id))

        return (
            <div className="report-research-section">
                <div className="report-research-header">
                    <label className="report-research-select-all">
                        <input
                            type="checkbox"
                            checked={allChecked}
                            onChange={() => this.toggleSelectAllFor(allResearchLogs)}
                        />
                        <span>Contacted via Research ({allResearchLogs.length})</span>
                    </label>
                </div>
                <div className="report-research-groups">
                    {groups.map(group => {
                        const groupChecked = group.logs.every(l => checkedIds.includes(l.id))
                        const someChecked = !groupChecked && group.logs.some(l => checkedIds.includes(l.id))
                        const hasUnread = group.logs.some(l => !l.is_read)
                        return (
                            <div
                                key={group.category}
                                className="report-research-category-row"
                                onClick={() => this.markGroupRead(group.logs)}
                            >
                                <input
                                    type="checkbox"
                                    checked={groupChecked}
                                    ref={(el) => { if (el) el.indeterminate = someChecked }}
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={() => this.toggleSelectAllFor(group.logs)}
                                />
                                <span className="report-research-category-name">
                                    {hasUnread && <span className="report-unread-dot" />}
                                    {group.category}
                                </span>
                                <span className="report-research-category-count">{group.logs.length}</span>
                            </div>
                        )
                    })}
                </div>
            </div>
        )
    }

    renderSentReports = () => {
        const { sentReports, sentReportsLoading, sentReportsError, sentReportsPagination, sentReportsPage } = this.state
        return (
            <div className="report-sent-section">
                <h2 className="report-sent-title">Sent Reports</h2>

                {sentReportsLoading && (
                    <div className="outreach-state">
                        <div className="outreach-spinner" />
                    </div>
                )}

                {!sentReportsLoading && sentReportsError && (
                    <div className="outreach-banner outreach-banner-error">
                        <span className="outreach-banner-text">{sentReportsError}</span>
                        <button className="outreach-banner-action" onClick={this.loadSentReports}>
                            Retry
                        </button>
                    </div>
                )}

                {!sentReportsLoading && !sentReportsError && sentReports.length === 0 && (
                    <div className="outreach-state outreach-empty">
                        No reports sent yet.
                    </div>
                )}

                {!sentReportsLoading && !sentReportsError && sentReports.length > 0 && (
                    <>
                        <div className="outreach-table-wrap">
                            <table className="outreach-table">
                                <thead>
                                    <tr>
                                        <th>Sent</th>
                                        <th>Recipient</th>
                                        <th>Entries</th>
                                        <th>Status</th>
                                        <th>Sent By</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sentReports.map(report => (
                                        <tr key={report.id}>
                                            <td className="outreach-muted">{getRoundedLocalDateTimeString(report.sent_at)}</td>
                                            <td className="outreach-muted">{report.chat_id}</td>
                                            <td className="outreach-muted">{report.entry_count}</td>
                                            <td>
                                                <span className={`report-sent-status-chip ${report.success ? 'report-sent-status-success' : 'report-sent-status-failed'}`}>
                                                    {report.success ? 'Sent' : 'Failed'}
                                                </span>
                                            </td>
                                            <td className="outreach-muted">{report.sent_by?.email || '—'}</td>
                                            <td>
                                                <button className="outreach-edit-btn" onClick={() => this.openReportDetail(report)}>
                                                    View
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {sentReportsPagination && sentReportsPagination.total_pages > 1 && (
                            <div className="outreach-pagination">
                                <span className="outreach-pagination-info">
                                    Page {sentReportsPagination.current_page} of {sentReportsPagination.total_pages}
                                </span>
                                <div className="outreach-pagination-controls">
                                    <button
                                        className="outreach-pagination-btn"
                                        disabled={!sentReportsPagination.has_previous}
                                        onClick={() => this.goToSentReportsPage(sentReportsPage - 1)}
                                    >
                                        Previous
                                    </button>
                                    <button
                                        className="outreach-pagination-btn"
                                        disabled={!sentReportsPagination.has_next}
                                        onClick={() => this.goToSentReportsPage(sentReportsPage + 1)}
                                    >
                                        Next
                                    </button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        )
    }

    renderReportDetailModal = () => {
        const { viewingReport, viewingReportLoading, viewingReportError, showRawMessage } = this.state
        if (!viewingReportLoading && !viewingReportError && !viewingReport) return null

        return (
            <div className="outreach-modal-overlay" onClick={this.closeReportDetail}>
                <div className="outreach-modal report-detail-modal" onClick={(e) => e.stopPropagation()}>
                    <h3 className="outreach-modal-title">Report Details</h3>

                    {viewingReportLoading && (
                        <div className="outreach-state">
                            <div className="outreach-spinner" />
                        </div>
                    )}

                    {!viewingReportLoading && viewingReportError && (
                        <div className="outreach-banner outreach-banner-error">
                            <span className="outreach-banner-text">{viewingReportError}</span>
                        </div>
                    )}

                    {!viewingReportLoading && viewingReport && (
                        <>
                            <p className="outreach-modal-subtitle">
                                Sent {getRoundedLocalDateTimeString(viewingReport.sent_at)} to <strong>{viewingReport.chat_id}</strong>
                                {' — '}
                                {viewingReport.success ? 'delivered successfully' : `failed: ${viewingReport.error}`}
                            </p>

                            <div className="report-detail-entries">
                                {viewingReport.entries.map(entry => (
                                    <div key={entry.id} className="report-detail-entry">
                                        <span className="outreach-company-name">{entry.lead_company_name || 'Lead'}</span>
                                        <div className="report-status-change">
                                            <span className={`report-status-chip lead-status-${entry.old_status}`}>
                                                {STATUS_LABELS[entry.old_status] || entry.old_status || '—'}
                                            </span>
                                            <span className="report-status-arrow">→</span>
                                            <span className={`report-status-chip lead-status-${entry.new_status}`}>
                                                {STATUS_LABELS[entry.new_status] || entry.new_status}
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <button type="button" className="report-raw-toggle" onClick={this.toggleRawMessage}>
                                {showRawMessage ? 'Hide raw message' : 'Show raw message'}
                            </button>
                            {showRawMessage && <pre className="report-raw-message">{viewingReport.message_text}</pre>}
                        </>
                    )}

                    <button className="outreach-modal-cancel" onClick={this.closeReportDetail}>
                        Close
                    </button>
                </div>
            </div>
        )
    }

    render(): ReactNode {
        const { logs, loading, error, needsAuth, checkedIds, additionalMessage, markingAllRead, sending, sendError, sendMessage } = this.state
        const tableLogs = this.tableLogs

        return (
            <div className="outreach-page">
                <div className="outreach-header">
                    <div className="outreach-title-header">
                        <h1 className="outreach-title">Report</h1>
                        <p className="outreach-subtitle">
                            {logs.length > 0
                                ? `${logs.length} status change${logs.length === 1 ? '' : 's'} not yet reported`
                                : 'Status changes to report to Telegram'}
                        </p>
                    </div>
                    {logs.length > 0 && (
                        <button className="outreach-edit-btn" onClick={this.handleMarkAllRead} disabled={markingAllRead}>
                            {markingAllRead ? 'Marking...' : 'Mark All Read'}
                        </button>
                    )}
                </div>

                {!loading && needsAuth && (
                    <div className="outreach-banner outreach-banner-info">
                        <span className="outreach-banner-text">Sign in to view status reports.</span>
                        <button className="outreach-banner-action" onClick={() => controllers().modals.authModal.show()}>
                            Sign In
                        </button>
                    </div>
                )}

                {!loading && error && (
                    <div className="outreach-banner outreach-banner-error">
                        <span className="outreach-banner-text">{error}</span>
                        <button className="outreach-banner-action" onClick={this.loadLogs}>
                            Retry
                        </button>
                    </div>
                )}

                {loading && (
                    <div className="outreach-state">
                        <div className="outreach-spinner" />
                    </div>
                )}

                {!loading && !error && !needsAuth && logs.length === 0 && (
                    <div className="outreach-state outreach-empty">
                        Nothing to report — every status change has already been sent.
                    </div>
                )}

                {!loading && !error && !needsAuth && logs.length > 0 && (
                    <>
                        {tableLogs.length > 0 && this.renderTable(tableLogs)}
                        {this.renderResearchContactedSummary()}

                        <div className="category-form-group report-note-group">
                            <label className="category-form-label">Note (optional)</label>
                            <textarea
                                className="category-form-textarea"
                                value={additionalMessage}
                                onChange={this.handleAdditionalMessageChange}
                                placeholder="Appended once at the end of the Telegram message — not tied to any single entry."
                                rows={2}
                                disabled={sending}
                            />
                        </div>

                        {sendError && <div className="category-form-error report-send-error">{sendError}</div>}
                        {sendMessage && (
                            <div className="outreach-banner outreach-banner-info">
                                <span className="outreach-banner-text">{sendMessage}</span>
                            </div>
                        )}

                        <div className="report-send-bar">
                            <span className="outreach-muted">
                                {checkedIds.length} of {logs.length} selected
                            </span>
                            <button className="outreach-add-btn" onClick={this.handleSend} disabled={sending || !checkedIds.length}>
                                {sending ? 'Sending...' : 'Send Report'}
                            </button>
                        </div>
                    </>
                )}

                {this.renderSentReports()}
                {this.renderReportDetailModal()}
            </div>
        )
    }
}

export { ReportPage }